import type { PlatformType } from '@/types'

type MessageHandler = (data: unknown) => void

export class AppNativeSDK {
    private readonly platform: PlatformType
    private readonly messageHandlers = new Set<MessageHandler>()
    private initialized = false

    constructor(platform: PlatformType) {
        this.platform = platform
    }

    get isAvailable(): boolean {
        return !!this.sdk
    }

    // We use window.top because in WebView iframes, native app methods
    // are only accessible on the top-level window
    private get sdk() {
        if (this.platform === 'android') {
            return window.top?.PlaygamaApp
        }
        if (this.platform === 'ios') {
            return window.top?.webkit?.messageHandlers?.playgamaApp
        }
        return undefined
    }

    init() {
        if (this.initialized || !this.isAvailable) {
            return
        }
        this.initialized = true
        // Listen on top window for messages from native app
        window.top?.addEventListener('message', (event) => {
            this.handleMessage(event.data)
        })
    }

    private handleMessage(data: unknown) {
        if (!data || typeof data !== 'object') {
            return
        }
        for (const handler of this.messageHandlers) {
            handler(data)
        }
    }

    postMessage(type: string, payload: Record<string, unknown>) {
        return this.sdk?.postMessage(JSON.stringify({ type, payload }))
    }

    addMessageHandler(handler: MessageHandler) {
        this.messageHandlers.add(handler)
        return () => this.removeMessageHandler(handler)
    }

    removeMessageHandler(handler: MessageHandler) {
        this.messageHandlers.delete(handler)
    }

    dispose() {
        this.messageHandlers.clear()
    }
}
